import type { Currency, DealChannel, DealStatus, LeadStage } from "../../api/salesApi";

/* «מכירות» — shared formatting and labels. Money from the server is in agorot / cents (integers). */

export type Tone = "success" | "neutral" | "danger" | "info" | "warning" | "primary" | "violet";

const ILS = new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 });
const CUR: Partial<Record<Currency, Intl.NumberFormat>> = {};

/** ₪ 1,234 from agorot */
export const fmtIls = (agorot: number | null | undefined) => ILS.format(Math.round(Number(agorot ?? 0) / 100) || 0);

/** an amount in its own currency (minor units) */
export function fmtMoney(minor: number | null | undefined, currency: Currency = "ILS"): string {
  if (currency === "ILS") return fmtIls(minor);
  let f = CUR[currency];
  if (!f) {
    f = new Intl.NumberFormat("he-IL", { style: "currency", currency, maximumFractionDigits: 0 });
    CUR[currency] = f;
  }
  return f.format(Math.round(Number(minor ?? 0) / 100) || 0);
}

export const CURRENCY_SIGN: Record<Currency, string> = { ILS: "₪", USD: "$", GBP: "£", EUR: "€" };

export const fmtNum = (n: number | null | undefined) => Number(n ?? 0).toLocaleString("he-IL");

/** user input in shekels / dollars → minor units (null when empty / invalid) */
export function toMinor(text: string): number | null {
  const n = Number(String(text).replace(/[,\s₪$]/g, ""));
  if (!String(text).trim() || !Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/** minor units → an input value in shekels / dollars */
export const fromMinor = (minor: number | null | undefined) => (minor ? String(Math.round(minor) / 100) : "");

/** YYYY-MM-DD → dd/mm/yyyy */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** seconds → m:ss */
export function fmtSecs(s: number | null | undefined): string {
  const n = Math.max(0, Math.round(Number(s ?? 0)));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
}

/** today in Israel time, YYYY-MM-DD (same as the server) */
export function todayIso(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jerusalem" }).format(now);
}

export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

const isoOf = (y: number, m0: number, d: number) => new Date(Date.UTC(y, m0, d)).toISOString().slice(0, 10);

export type QuickPeriod = "30d" | "month" | "prev" | "year";

/** this month / last month / this year as from–to */
export function periodRange(p: QuickPeriod, today = todayIso()): { from: string; to: string } {
  const [y, m] = today.split("-").map(Number);
  if (p === "year") return { from: isoOf(y, 0, 1), to: isoOf(y, 11, 31) };
  if (p === "30d") {
    const d = new Date(`${today}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 29);
    return { from: d.toISOString().slice(0, 10), to: today };
  }
  if (p === "prev") return { from: isoOf(y, m - 2, 1), to: isoOf(y, m - 1, 0) };
  return { from: isoOf(y, m - 1, 1), to: isoOf(y, m, 0) };
}

/** "YYYY-MM" for the current month, shifted by `delta` months */
export function monthKey(delta = 0, today = todayIso()): string {
  const [y, m] = today.split("-").map(Number);
  return isoOf(y, m - 1 + delta, 1).slice(0, 7);
}

const MONTHS = ["ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"];
const MONTHS_SHORT = ["ינו׳", "פבר׳", "מרץ", "אפר׳", "מאי", "יוני", "יולי", "אוג׳", "ספט׳", "אוק׳", "נוב׳", "דצמ׳"];

export function monthLabel(key: string, short = false): string {
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return short ? `${MONTHS_SHORT[m - 1]} ${String(y).slice(2)}` : `${MONTHS[m - 1]} ${y}`;
}

export const STAGE_META: Record<LeadStage, { label: string; tone: Tone }> = {
  new: { label: "חדש", tone: "info" },
  in_progress: { label: "בטיפול", tone: "primary" },
  won: { label: "נסגר בהצלחה", tone: "success" },
  lost: { label: "לא רלוונטי", tone: "neutral" },
  done: { label: "טופל", tone: "violet" },
};

export const CHANNEL_LABEL: Record<DealChannel, string> = { rep: "נציג", self_service: "דרך הקו", partial: "עסקה חלקית" };

export const DEAL_STATUS: Record<DealStatus, { label: string; tone: Tone }> = {
  open: { label: "פתוחה", tone: "warning" },
  paid: { label: "שולמה", tone: "success" },
  cancelled: { label: "בוטלה", tone: "neutral" },
};

export const REGION_LABEL: Record<string, string> = { domestic: "ישראל", abroad: "חו״ל" };

export const errorMessage = (e: unknown, fallback: string) => (e as { message?: string })?.message || fallback;

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** a Select's options with an "all" (or "none") first entry */
export function withEmpty(label: string, options: Array<{ value: string; label: string }>): Array<{ value: string; label: string }> {
  return [{ value: "", label }, ...options];
}
